import {
  IconButton,
  FormControl,
  OutlinedInput,
  FormHelperText,
} from "@mui/material";
import * as React from "react";
import { Formik } from "formik";
import { styles } from "./styles";
import Box from "@mui/material/Box";
import Loader from "components/loader";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import { useDispatch } from "react-redux";
import { Close } from "@mui/icons-material";
import { FOLDER } from "utils/constants";
import { CarCompanySchema } from "utils/schema";
import Typography from "@mui/material/Typography";
import AnimateButton from "components/animateButton";
import { UPLOAD_FILE_TO_S3 } from "utils/services";
import { validateFile } from "modules/userManagement/action";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import { CompanyDetails } from "utils/modals";
import {
  addNewCompany,
  updateCompany,
} from "modules/company/action";

export default function AddNewCompanyModal({
  state,
  setState,
  selectedCompany,
}: any) {
  const dispatch: any = useDispatch();

  const [loading, setLoading] = React.useState(false);

  // Store the actual selected File locally.
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);

  // Store the local preview URL separately from the actual logo URL.
  const [previewUrl, setPreviewUrl] = React.useState<string>("");

  const fileRef = React.useRef<HTMLInputElement>(null);

  const handleClose = () => {
    setState(false);

    // Cleanup local object URL.
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewUrl("");
    setSelectedFile(null);

    if (fileRef.current) {
      fileRef.current.value = "";
    }
  };

  const handleOnSubmit = async (
    values: CompanyDetails,
    action: any
  ) => {
    setLoading(true);

    try {
      let logoUrl = values.logo;

      /*
       * Upload the image ONLY when Add/Update API is actually
       * going to be called.
       */
      if (selectedFile) {
        logoUrl = await UPLOAD_FILE_TO_S3(
          selectedFile,
          FOLDER.VEHICLE_BRAND
        );

              }

      if (selectedCompany) {
        // UPDATE COMPANY
        dispatch(
          updateCompany({
            data: {
              id: selectedCompany.id,
              name: values.name,
              logo: logoUrl,
            },
            callback: (result?: any) => {
              setLoading(false);
              if (result) {
                action.resetForm();
                handleClose();
              }
            },
          })
        );
      } else {
        // ADD COMPANY
        dispatch(
          addNewCompany({
            data: {
              name: values.name,
              logo: logoUrl,
            },
            callback: (result?: any) => {
              setLoading(false);
              if (result) {
                action.resetForm();
                handleClose();
              }
            },
          })
        );
      }
    } catch (err: any) {
      setLoading(false);
    }
  };

  const onImagePicked = (
    evt: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: Function
  ) => {
    if (
      !evt ||
      !evt.target ||
      !evt.target.files ||
      evt.target.files.length === 0
    ) {
      return;
    }

    const file = evt.target.files[0];

    validateFile(file, 900)
      .then(() => {
        /*
         * DO NOT upload to S3 here.
         *
         * Store the actual File locally.
         */
        setSelectedFile(file);

        /*
         * Create a temporary local URL for preview.
         */
        const localUrl = URL.createObjectURL(file);

        // Cleanup previous preview URL.
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }

        setPreviewUrl(localUrl);

        /*
         * We still set a value for Formik so that validation
         * knows that a logo has been selected.
         *
         * The actual S3 URL will be set only after upload
         * during form submission.
         */
        setFieldValue("logo", localUrl);
      })
      .catch(() => {});
  };

  const onUploadImageClick = () => {
    fileRef.current?.click();
  };

  return (
    <Modal
      open={state}
      aria-labelledby="add-category-modal-title"
      aria-describedby="add-category-modal-description"
    >
      <Box sx={styles.addCategory}>
        <Box sx={styles.headingBar}>
          <Typography
            sx={styles.heading_ac}
            variant="h3"
            component="h2"
          >
            {selectedCompany
              ? "Update Company"
              : "Add new Company"}
          </Typography>

          <IconButton
            onClick={handleClose}
            sx={styles.icon}
          >
            <Close />
          </IconButton>
        </Box>

        <Formik
          initialValues={
            selectedCompany
              ? {
                logo: selectedCompany.logo,
                name: selectedCompany.name,
                id: selectedCompany.id,
              }
              : { ...new CompanyDetails() }
          }
          validationSchema={CarCompanySchema}
          onSubmit={handleOnSubmit}
        >
          {({
            errors,
            handleBlur,
            handleSubmit,
            touched,
            values,
            setFieldValue,
          }) => {
            return (
              <form
                noValidate
                onSubmit={handleSubmit}
              >
                <FormControl
                  sx={styles.inputBox}
                  error={Boolean(
                    touched.name && errors.name
                  )}
                >
                  <OutlinedInput
                    fullWidth
                    required
                    type="text"
                    name="name"
                    onBlur={handleBlur}
                    onChange={({ target }: any) => {
                      const value = target.value;

                      setFieldValue(
                        "name",
                        value
                          .replace(/[0-9]+/g, "")
                          .trimStart()
                      );
                    }}
                    value={values.name}
                    placeholder="Enter Company Name"
                    inputProps={{
                      maxLength: 15,
                    }}
                  />

                  {touched.name && errors.name && (
                    <FormHelperText
                      error
                      id="standard-weight-helper-text-name"
                    >
                      {String(errors.name)}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl
                  sx={[
                    styles.inputBox2,
                    { paddingTop: 0 },
                  ]}
                  error={Boolean(
                    touched.logo && errors.logo
                  )}
                >
                  <Box sx={styles.selectPhotoBtn}>
                    <Box sx={styles.photoInput}>
                      {previewUrl || values.logo ? (
                        <div>
                          <img
                            alt="logo"
                            style={styles.image}
                            src={previewUrl || values.logo}
                            onClick={onUploadImageClick}
                          />
                        </div>
                      ) : (
                        <Typography
                          onClick={onUploadImageClick}
                          style={{
                            padding: "10px 20px",
                            display: "flex",
                            alignItems: "center",
                            gap: "0 10px",
                          }}
                        >
                          Upload Company Logo

                          <AddToPhotosIcon
                            sx={{
                              fill: "royalblue",
                            }}
                          />
                        </Typography>
                      )}
                    </Box>

                    <input
                      id="fileInput"
                      type="file"
                      ref={fileRef}
                      accept=".jpg,.jpeg,.png"
                      style={{ display: "none" }}
                      onChange={(e) =>
                        onImagePicked(
                          e,
                          setFieldValue
                        )
                      }
                    />
                  </Box>

                  {touched.logo && errors.logo && (
                    <FormHelperText error>
                      {String(errors.logo)}
                    </FormHelperText>
                  )}
                </FormControl>

                <Box sx={styles.button}>
                  <AnimateButton>
                    <Button
                      disableElevation
                      size="large"
                      type="submit"
                      variant="contained"
                      color="secondary"
                      disabled={loading}
                    >
                      {selectedCompany
                        ? "Update Company"
                        : "Add New Company"}
                    </Button>
                  </AnimateButton>
                </Box>
              </form>
            );
          }}
        </Formik>

        {loading && <Loader />}
      </Box>
    </Modal>
  );
}
